import React, { useCallback, useReducer } from 'react';
import { IItemForGetShortInfo, TFileId, IFileData, ICreateCustomer, TUseTipsByDefault } from 'types';
import {
	CreateCustomerContext,
	createCustomerInitialState,
	TCreateCustomerSelectedClientType,
	TCreateCustomerSelectedItem,
	TTypeOfCreateCustomerModalWindow,
	TSourceUrlLocally,
} from './createCustomerContext';
import { createCustomerReducer } from './createCustomerReducer';
import { CreateCustomerActionTypes } from './createCustomerActions';

const CreateCustomerState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(createCustomerReducer, createCustomerInitialState);

	const setFile = useCallback((fileId: TFileId, sourceUrlLocally: TSourceUrlLocally) => {
		dispatch({
			type: CreateCustomerActionTypes.SET_FILE,
			payload: {
				fileId: fileId,
				sourceUrlLocally: sourceUrlLocally,
			},
		});
	}, []);

	const changeName = useCallback((value: string) => {
		dispatch({
			type: CreateCustomerActionTypes.CHANGE_NAME,
			payload: {
				name: value,
			},
		});
	}, []);

	const changeEmail = useCallback((value: string) => {
		dispatch({
			type: CreateCustomerActionTypes.CHANGE_EMAIL,
			payload: {
				email: value,
			},
		});
	}, []);

	const changeEmailBodyTemplate = useCallback((value: string) => {
		dispatch({
			type: CreateCustomerActionTypes.CHANGE_EMAIL_BODY_TEMPLATE,
			payload: {
				emailBodyTemplate: value,
			},
		});
	}, []);

	const setClientType = useCallback((value: TCreateCustomerSelectedClientType) => {
		dispatch({
			type: CreateCustomerActionTypes.SET_CLIENT_TYPE,
			payload: {
				clientType: value,
			},
		});
	}, []);

	const changeBillTemplateFileData = useCallback((value: IFileData) => {
		dispatch({
			type: CreateCustomerActionTypes.CHANGE_BILL_TEMPLATE_FILE_DATA,
			payload: {
				billTemplateFileData: value,
			},
		});
	}, []);

	const changeBillAdvertisingImageData = useCallback((value: IFileData) => {
		dispatch({
			type: CreateCustomerActionTypes.CHANGE_BILL_ADVERTISING_IMAGE_DATA,
			payload: {
				billAdvertisingImageData: value,
			},
		});
	}, []);

	const changeBillAdvertisingLink = useCallback((value: string) => {
		dispatch({
			type: CreateCustomerActionTypes.CHANGE_BILL_ADVERTISING_LINK,
			payload: {
				billAdvertisingLink: value,
			},
		});
	}, []);

	const changePriceType = useCallback((value: string) => {
		dispatch({
			type: CreateCustomerActionTypes.CHANGE_PRICE_TYPE,
			payload: {
				priceType: value,
			},
		});
	}, []);

	const setTips0 = useCallback((value: Nullable<number>) => {
		dispatch({
			type: CreateCustomerActionTypes.SET_TIPS_0,
			payload: {
				tips0: value,
			},
		});
	}, []);

	const setTips1 = useCallback((value: Nullable<number>) => {
		dispatch({
			type: CreateCustomerActionTypes.SET_TIPS_1,
			payload: {
				tips1: value,
			},
		});
	}, []);

	const setTips2 = useCallback((value: Nullable<number>) => {
		dispatch({
			type: CreateCustomerActionTypes.SET_TIPS_2,
			payload: {
				tips2: value,
			},
		});
	}, []);

	const setTips3 = useCallback((value: Nullable<number>) => {
		dispatch({
			type: CreateCustomerActionTypes.SET_TIPS_3,
			payload: {
				tips3: value,
			},
		});
	}, []);

	const setDefaultTips = useCallback(
		(value: TUseTipsByDefault) => {
			dispatch({
				type: CreateCustomerActionTypes.SET_DEFAULT_TIPS,
				payload: {
					defaultTips: state.defaultTips === value ? null : value,
				},
			});
		},
		[state.defaultTips],
	);

	const setAds = useCallback((value: string) => {
		dispatch({
			type: CreateCustomerActionTypes.SET_ADS,
			payload: {
				ads: value,
			},
		});
	}, []);

	const setMixpanelToken = useCallback((value: string) => {
		dispatch({
			type: CreateCustomerActionTypes.SET_MIXPANEL_TOKEN,
			payload: {
				mixpanelToken: value,
			},
		});
	}, []);

	const setStopListSyncIntervalMinutes = useCallback((value: Nullable<number>) => {
		dispatch({
			type: CreateCustomerActionTypes.SET_STOP_LIST_SYNC_INTERVAL_MINUTES,
			payload: {
				stopListSyncIntervalMinutes: value,
			},
		});
	}, []);

	const setFetchedLanguages = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateCustomerActionTypes.SET_FETCHED_LANGUAGES,
			payload: {
				fetchedLanguages: value,
			},
		});
	}, []);

	const setCustomerDefaultLanguage = useCallback((value: TCreateCustomerSelectedItem) => {
		dispatch({
			type: CreateCustomerActionTypes.SET_ID_CUSTOMER_DEFAULT_LANGUAGE,
			payload: {
				IDSelectedCustomerDefaultLanguage: value,
			},
		});
	}, []);

	const setCustomerLanguages = useCallback((value: string[]) => {
		dispatch({
			type: CreateCustomerActionTypes.SET_IDs_CUSTOMER_LANGUAGES,
			payload: {
				IDsSelectedCustomerLanguages: value,
			},
		});
	}, []);

	const setDataForSubmit = useCallback((value: Nullable<ICreateCustomer>) => {
		dispatch({
			type: CreateCustomerActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfCreateCustomerModalWindow) => {
		dispatch({
			type: CreateCustomerActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const resetFields = useCallback(() => dispatch({ type: CreateCustomerActionTypes.RESET_FIELDS }), []);

	return (
		<CreateCustomerContext.Provider
			value={{
				setFile,
				changeName,
				changeEmail,
				changeEmailBodyTemplate,
				setClientType,
				changeBillTemplateFileData,
				changeBillAdvertisingImageData,
				changeBillAdvertisingLink,
				changePriceType,
				setTips0,
				setTips1,
				setTips2,
				setTips3,
				setDefaultTips,
				setAds,
				setMixpanelToken,
				setStopListSyncIntervalMinutes,
				setFetchedLanguages,
				setCustomerDefaultLanguage,
				setCustomerLanguages,
				setDataForSubmit,
				changeTypeOfModalWindow,
				resetFields,
				fileId: state.fileId,
				sourceUrlLocally: state.sourceUrlLocally,
				name: state.name,
				email: state.email,
				emailBodyTemplate: state.emailBodyTemplate,
				clientType: state.clientType,
				billTemplateFileData: state.billTemplateFileData,
				billAdvertisingImageData: state.billAdvertisingImageData,
				billAdvertisingLink: state.billAdvertisingLink,
				priceType: state.priceType,
				tips0: state.tips0,
				tips1: state.tips1,
				tips2: state.tips2,
				tips3: state.tips3,
				defaultTips: state.defaultTips,
				ads: state.ads,
				mixpanelToken: state.mixpanelToken,
				stopListSyncIntervalMinutes: state.stopListSyncIntervalMinutes,
				fetchedLanguages: state.fetchedLanguages,
				IDSelectedCustomerDefaultLanguage: state.IDSelectedCustomerDefaultLanguage,
				IDsSelectedCustomerLanguages: state.IDsSelectedCustomerLanguages,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</CreateCustomerContext.Provider>
	);
};

export default CreateCustomerState;
